import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

/**
 * One static query for the parent info page's own photography, which lives in
 * src/images/schools/parent-info. Sections still without a chosen photo borrow
 * from ../uk/images. Gatsby hoists static queries at build time, so this has
 * to stay a literal — don't parameterise it.
 */
const useParentInfoImages = () =>
  useStaticQuery(graphql`
    {
      hero: file(relativePath: { eq: "schools/parent-info/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, placeholder: NONE, layout: FULL_WIDTH)
        }
      }
      together: file(
        relativePath: { eq: "schools/parent-info/parent-qa.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1000)
        }
      }
      faq: file(
        relativePath: { eq: "schools/parent-info/parent-questions.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1000)
        }
      }
      cardLessons: file(
        relativePath: { eq: "schools/parent-info/lessons.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 800, backgroundColor: "#ffffff")
        }
      }
      cardHelp: file(
        relativePath: { eq: "schools/parent-info/help_button.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 800, backgroundColor: "#ffffff")
        }
      }
      cardFeed: file(relativePath: { eq: "schools/parent-info/feed.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 800, backgroundColor: "#ffffff")
        }
      }
      badge: file(relativePath: { eq: "schools/parent-info/protected.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 800
            placeholder: NONE
            backgroundColor: "transparent"
          )
        }
      }
    }
  `)

type Slot =
  | "hero"
  | "together"
  | "faq"
  | "badge"
  | "cardLessons"
  | "cardHelp"
  | "cardFeed"

/**
 * All three photos are 4:3 with faces in the upper half, while the slots they
 * fill are short and wide on mobile, so `objectPosition` sits above centre to keep
 * faces in frame rather than desks and laps.
 */
const makeImage =
  (slot: Slot, alt: string, objectPosition: string) =>
  // `className` is required: Hero and Divided always pass one.
  ({ className }: { className: string }) => {
    const data = useParentInfoImages()
    return (
      <GatsbyImage
        image={getImage(data[slot])}
        className={className}
        style={{ width: "100%", height: "100%" }}
        imgStyle={{ objectFit: "cover", objectPosition }}
        alt={alt}
      />
    )
  }

export const HeroImage = makeImage(
  "hero",
  "Pupils working together at a classroom table with their teacher",
  "center 30%",
)

export const TogetherImage = makeImage(
  "together",
  "A parent on the sofa looking at a phone with two young children",
  "center 30%",
)

export const FaqImage = makeImage(
  "faq",
  "A parent and a teenager laughing together on the sofa",
  "center 35%",
)

/**
 * The "Protected by SomeBuddy" badge is a transparent PNG, so unlike the
 * photos it is shown whole and unmasked: no cover crop, and no blurred or
 * coloured placeholder that would paint a box behind it while it loads.
 */
export const BadgeImage = ({ className }: { className: string }) => {
  const data = useParentInfoImages()
  return (
    <GatsbyImage
      image={getImage(data.badge)}
      className={className}
      imgStyle={{ objectFit: "contain" }}
      alt="Protected by SomeBuddy – Online Safety badge"
    />
  )
}

/**
 * Product shots for the three "How SomeBuddy helps" cards. Each is a whole
 * device centred on white with room around it, so it is shown whole
 * (`contain`) rather than cropped like the photos, and its white ground
 * blends into the card's white panel.
 */
const makeCardImage =
  (slot: Slot, alt: string) =>
  ({ className }: { className: string }) => {
    const data = useParentInfoImages()
    return (
      <GatsbyImage
        image={getImage(data[slot])}
        className={className}
        imgStyle={{ objectFit: "contain" }}
        alt={alt}
      />
    )
  }

export const CardImages = {
  lessons: makeCardImage(
    "cardLessons",
    "SomeBuddy's online safety lessons for teachers on a tablet",
  ),
  help: makeCardImage(
    "cardHelp",
    "The Get Help screen in the SomeBuddy app: Got a worry? Tell SomeBuddy.",
  ),
  feed: makeCardImage(
    "cardFeed",
    "Online safety bites and guides in the SomeBuddy app",
  ),
}
